import React from "react";
import { motion } from "framer-motion";
import { Package } from "lucide-react";

const PRODUCT_EXPORT_FRONT = "/images/ProductType-Export-Front.jpeg";
const PRODUCT_EXPORT_BACK = "/images/ProductType-Export-Back.jpeg";
const PRODUCT_PILLOW_SMALL = "/images/ProductType-Pillow-Pouch-Small.jpeg";
const PRODUCT_PILLOW_MEDIUM = "/images/ProductType-Pillow-Pouch-Medium.jpeg";
const PRODUCT_TRANSPARENT = "/images/ProductType-TranspareParent-Pouch.jpeg";

const PRODUCT_TYPES = [
    {
        id: "export-front",
        name: "Export",
        image: PRODUCT_EXPORT_FRONT
    },
    {
        id: "export-back",
        name: "Export",
        image: PRODUCT_EXPORT_BACK
    },
    {
        id: "pillow-small",
        name: "Pillow Pouch - Small",
        image: PRODUCT_PILLOW_SMALL
    },
    {
        id: "pillow-medium",
        name: "Pillow Pouch - Medium",
        image: PRODUCT_PILLOW_MEDIUM
    },
    {
        id: "transparent",
        name: "Transparent",
        image: PRODUCT_TRANSPARENT
    }
];

export default function ProductCatalog() {
    return (
        <div className="min-h-screen text-white bg-gradient-to-b from-black via-gray-900 to-black">
            {/* Header Section */}
            <section className="relative py-24 px-6 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-yellow-500/5 to-amber-500/5 animate-pulse" />
                
                <div className="relative max-w-7xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-8 py-3 mb-8">
                            <Package size={16} className="text-amber-400" />
                            <span className="text-sm font-medium tracking-wide uppercase text-white">Our Products</span>
                        </div>
                        
                        <h1 className="text-5xl md:text-7xl font-black tracking-tighter mb-6">
                            <span className="bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400 bg-clip-text text-transparent">
                                Product Catalog
                            </span>
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                            Explore our range of premium makhana packaging options, designed for quality, freshness, and convenience.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Product Grid */}
            <section className="relative py-16 px-6">
                <div className="relative max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {PRODUCT_TYPES.map((product, index) => (
                            <motion.div
                                key={product.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1, duration: 0.6 }}
                                whileHover={{ 
                                    y: -8,
                                    boxShadow: "0 20px 40px rgba(251, 191, 36, 0.2)"
                                }}
                                className="relative group cursor-pointer"
                            >
                                <div className="relative bg-gradient-to-br from-gray-900/90 via-gray-800/80 to-gray-900/90 rounded-3xl p-6 border border-white/10 backdrop-blur-2xl overflow-hidden">
                                    <motion.div
                                        animate={{
                                            opacity: [0.3, 0.6, 0.3],
                                            scale: [1, 1.05, 1]
                                        }}
                                        transition={{
                                            duration: 3,
                                            repeat: Infinity,
                                            ease: "easeInOut",
                                            delay: index * 0.5
                                        }}
                                        className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-yellow-500/5 to-amber-500/10 rounded-3xl blur-xl"
                                    />
                                    
                                    <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-gray-800/50 mb-6">
                                        <img 
                                            src={product.image} 
                                            alt={product.name} 
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                                        
                                        <div className="absolute top-4 left-4 bg-black/50 backdrop-blur-md rounded-full px-4 py-2 border border-white/20">
                                            <span className="text-xs font-semibold text-amber-400 uppercase tracking-wider">
                                                {product.name}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="relative py-24 px-6">
                <div className="relative max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="bg-gradient-to-br from-gray-900/90 via-gray-800/80 to-gray-900/90 rounded-3xl p-12 border border-white/10 backdrop-blur-2xl"
                    >
                        <h2 className="text-3xl md:text-4xl font-black mb-4">
                            Looking for Custom Packaging?
                        </h2>
                        <p className="text-gray-300 text-lg mb-8">
                            We offer customized packaging solutions for bulk orders and business partnerships.
                        </p>
                        <motion.a
                            href="mailto:info@globicindia.com"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="inline-flex items-center gap-3 bg-gradient-to-br from-amber-500 to-orange-500 text-white rounded-full px-10 py-5 font-black text-xl shadow-2xl hover:shadow-amber-400/60 transition-all border border-white/20"
                        >
                            Contact Us
                        </motion.a>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}
